import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChartCard, chartColors, type ChartSeries } from '../components/ChartCard';
import { FilterChip } from '../components/FilterChip';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { findSportMetric } from '../data/sportMetrics';
import { useLocale } from '../contexts/LocaleContext';
import { useMetrics } from '../contexts/MetricsContext';
import type { ChartRange } from '../types';
import { buildSeries, sliceRange } from '../utils/series';

/** Body evolution views. Composition never mixes in weight; weight is its own selectable metric. */
type BodyView = 'composition' | 'weight';
export function ProgressCharts() {
  const {
    metricId = 'body'
  } = useParams();
  const {
    t,
    tl
  } = useLocale();
  const metrics = useMetrics();
  const [range, setRange] = useState<ChartRange>('1y');
  const [bodyView, setBodyView] = useState<BodyView>('composition');
  const body = (id: string) => metrics.bodyMetrics.find(metric => metric.id === id);
  const weightAvailable = (body('weight')?.value ?? 0) > 0;
  const isBody = metricId === 'body';
  const view = isBody && bodyView === 'weight' && weightAvailable ? 'weight' : metricId;
  const sport = findSportMetric(decodeURIComponent(metricId));
  const series = useMemo<ChartSeries[] | null>(() => {
    if (view === 'body') {
      const ffm = body('ffm');
      const fat = body('bodyFat');
      const water = body('water');
      if (!ffm || !fat || !water) return null;
      return [{
        id: 'ffm',
        name: t('metrics.ffm'),
        kind: ffm.kind,
        points: sliceRange(buildSeries(ffm.value, 11, 0.04), range),
        color: chartColors.deep
      }, {
        id: 'bodyFat',
        name: t('metrics.bodyFat'),
        kind: fat.kind,
        points: sliceRange(buildSeries(fat.value, 23, 0.06), range),
        color: chartColors.gold
      }, {
        id: 'water',
        name: t('metrics.water'),
        kind: water.kind,
        points: sliceRange(buildSeries(water.value, 37, 0.03), range),
        color: chartColors.soft
      }];
    }
    if (view === 'weight') {
      const weight = body('weight');
      if (!weight) return null;
      return [{
        id: 'weight',
        name: t('metrics.weight'),
        kind: weight.kind,
        points: sliceRange(buildSeries(weight.value, 53, 0.03), range),
        color: chartColors.deep
      }];
    }
    if (sport) {
      return [{
        id: sport.id,
        name: tl(sport.label),
        kind: sport.kind,
        points: sliceRange(buildSeries(sport.value, 71, 0.05), range),
        color: chartColors.gold
      }];
    }
    return null;
  }, [view, range, sport, metrics.bodyMetrics, t, tl]);
  if (!series) {
    return <div className="px-2 pb-2" data-id="element-3431" data-mp="hd7euljq0y00">
        <PageHeader title={t('metrics.charts.title')} showBack data-id="element-3432" data-mp="201ih29vpfnlo" />
        <EmptyState title={t('metrics.charts.missing.title')} body={t('metrics.charts.missing.body')} data-id="element-3433" data-mp="203d1zo6z7fpr" />
      </div>;
  }
  const title = view === 'body' ? t('metrics.charts.body') : view === 'weight' ? t('metrics.weight') : tl(sport!.label);
  const question = view === 'body' ? t('metrics.charts.bodyQuestion') : view === 'weight' ? t('metrics.charts.weightQuestion') : t('metrics.charts.sportQuestion');
  return <div className="px-2 pb-2" data-id="element-3434" data-mp="2nxd1vts0pqsy">
      <PageHeader title={t('metrics.charts.title')} showBack data-id="element-3435" data-mp="2sqfzqe3tvmey" />

      {isBody && weightAvailable && <div className="mb-2" data-id="element-3436" data-mp="2wsaybaudjlp1">
          <p className="mb-1 px-1 text-[11px] font-medium text-muted" data-id="element-3437" data-mp="2wqhqvtbxsxfz">
            {t('metrics.charts.view.label')}
          </p>
          <ul className="flex flex-wrap gap-1" data-id="element-3438" data-mp="31orn879ee436">
            {(['composition', 'weight'] as const).map(item => <li key={item} data-id="element-3439" data-mp="2t38g1bdvuzzv">
                <FilterChip label={t(`metrics.charts.view.${item}`)} active={bodyView === item} onClick={() => setBodyView(item)} data-id="element-3440" data-mp="2t2gk1zgdj092" />
              </li>)}
          </ul>
        </div>}

      <ChartCard title={title} question={question} series={series} range={range} onRangeChange={setRange} data-id="element-3441" data-mp="2xtyjuk6nuusg" />

      {view === 'body' && <p className="mt-2 px-1 text-[11px] leading-snug text-muted" data-id="element-3442" data-mp="2a679vn8letuc">
          {t('metrics.charts.bodyNote')}
        </p>}
    </div>;
}